import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { CheckCircle2, Save, Building2, User, Hammer, Wrench, HardHat } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  Pending: "bg-stone-100 text-stone-700",
  "Partially Completed": "bg-amber-100 text-amber-800",
  Completed: "bg-emerald-100 text-emerald-800",
};

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-emerald-700" />
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold">{title}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function F({ label, name, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`kyc-${name}`}
        className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/10 transition text-sm"
      />
    </div>
  );
}

export default function KYCTab({ projectId }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => setData((await api.get(`/projects/${projectId}/kyc`)).data);
  useEffect(() => { load(); }, [projectId]);

  if (!data) return <div className="text-stone-500">Loading...</div>;

  const update = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.put(`/projects/${projectId}/kyc`, data);
      setData(updated);
      toast.success("KYC saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-stone-200 rounded-xl p-5 flex items-center justify-between">
        <div>
          <div className="text-base font-semibold text-stone-900">Client Onboarding KYC</div>
          <div className="text-xs text-stone-500 mt-1">Mandatory: Company PAN, GST, Primary Contact details · Auto-completes once 3+ consultants added.</div>
        </div>
        <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${STATUS_COLORS[data.status] || STATUS_COLORS.Pending}`}>
          {data.status === "Completed" && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />}
          {data.status}
        </span>
      </div>

      <Section icon={Building2} title="Company / KYC Documents">
        <F label="Company PAN" name="pan" value={data.pan} onChange={update} />
        <F label="GST Number" name="gst" value={data.gst} onChange={update} />
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Company Address</label>
          <textarea
            value={data.company_address || ""}
            onChange={(e) => update("company_address", e.target.value)}
            data-testid="kyc-company_address"
            rows={2}
            className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-emerald-700 text-sm"
          />
        </div>
        <F label="Authority Letter URL" name="authority_letter_url" value={data.authority_letter_url} onChange={update} />
        <F label="PAN Card File URL" name="pan_card_url" value={data.pan_card_url} onChange={update} />
        <F label="GST Certificate URL" name="gst_certificate_url" value={data.gst_certificate_url} onChange={update} />
      </Section>

      <Section icon={User} title="Primary Contact (Authorised Person)">
        <F label="Name" name="primary_contact_name" value={data.primary_contact_name} onChange={update} />
        <F label="Designation" name="primary_contact_designation" value={data.primary_contact_designation} onChange={update} />
        <F label="Phone" name="primary_contact_phone" value={data.primary_contact_phone} onChange={update} />
        <F label="Email" name="primary_contact_email" type="email" value={data.primary_contact_email} onChange={update} />
      </Section>

      <Section icon={Hammer} title="Architect">
        <F label="Architect Name" name="architect_name" value={data.architect_name} onChange={update} />
        <F label="Architect Firm" name="architect_firm" value={data.architect_firm} onChange={update} />
        <F label="Phone" name="architect_phone" value={data.architect_phone} onChange={update} />
        <F label="Email" name="architect_email" type="email" value={data.architect_email} onChange={update} />
      </Section>

      <Section icon={Wrench} title="MEP Consultant">
        <F label="MEP Consultant Name" name="mep_name" value={data.mep_name} onChange={update} />
        <F label="MEP Firm" name="mep_firm" value={data.mep_firm} onChange={update} />
        <F label="Phone" name="mep_phone" value={data.mep_phone} onChange={update} />
        <F label="Email" name="mep_email" type="email" value={data.mep_email} onChange={update} />
      </Section>

      <Section icon={HardHat} title="PMC / Project Management Consultant">
        <F label="PMC Name" name="pmc_name" value={data.pmc_name} onChange={update} />
        <F label="PMC Firm" name="pmc_firm" value={data.pmc_firm} onChange={update} />
        <F label="Phone" name="pmc_phone" value={data.pmc_phone} onChange={update} />
        <F label="Email" name="pmc_email" type="email" value={data.pmc_email} onChange={update} />
      </Section>

      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Notes</label>
        <textarea
          value={data.notes || ""}
          onChange={(e) => update("notes", e.target.value)}
          data-testid="kyc-notes"
          rows={3}
          className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-emerald-700 text-sm"
        />
      </div>

      <div className="flex justify-end sticky bottom-4">
        <button
          onClick={save}
          disabled={saving}
          data-testid="kyc-save"
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-2.5 rounded-lg text-sm font-medium shadow-lg transition disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save KYC"}
        </button>
      </div>
    </div>
  );
}
